import { useNavigate } from "react-router-dom";
import Loading from "../../components/Loading";
import PageLayout from "../../layouts/PageLayout";
import { compact, flatMap, map } from "lodash";
import {
  deleteImages,
  orderImagesUpload,
  useCreateStockListMutation,
  useUpdateStockListMutation
} from "../../utils/stockList.utils";
import Form from "../../ui/Form";
import BrandField from "./BrandField";
import ImageUploader from "../../ui/Form/ImageUploader";
import { useState } from "react";
import { onError } from "../../utils/toastify.utils";
import ForwardField from "./ForwardField";
import LocationField from "./LocationField";
import { dateTime } from "../../utils/date.utils";

export default function StockListAddPage({ editItem }) {
  const [onSubmitLoading, setOnSubmitLoading] = useState(false);
  const navigate = useNavigate();

  const id = editItem?.id ?? "";

  const prevImages = editItem?.images ?? [];
  const prevAuctionSheet = editItem?.auction_sheet ?? [];

  const [images, setImages] = useState(prevImages);
  const [auctionSheet, setAuctionSheet] = useState(prevAuctionSheet);

  const onBack = () => {
    navigate("/stock-list");
  };

  const { loading, createStockList } = useCreateStockListMutation(["createStockList"]);

  const { loading: updating, updateStockList } = useUpdateStockListMutation(["updateStockList"]);

  const fields = [
    {
      name: "brandField",
      type: "custom",
      customComponent: BrandField,
      col: { md: 12 }
    },
    {
      name: "locationField",
      type: "custom",
      customComponent: LocationField,
      col: { md: 12 }
    },
    {
      name: "forwardField",
      type: "custom",
      customComponent: ForwardField,
      col: { md: 12 }
    }
  ];

  const defaultValues = {
    ...editItem
  };

  const newImageUrls = images;
  const oldImageUrls = prevImages;

  const newAuctionImage = auctionSheet;
  const oldAuctionImage = prevAuctionSheet;

  const onSubmit = async (data) => {
    // loading start
    setOnSubmitLoading(true);

    const {
      chassis,
      chassis_code,
      features,
      asking_price,
      discount_price,
      forward_price,
      mileage,
      eta_date,
      ...rest
    } = data;

    // make full chassis name
    const fullChassis = chassis_code?.label + "-" + chassis;

    // upload images
    const uploadedImages = await orderImagesUpload(fullChassis, [...images]);
    const deletedImages = await deleteImages([...newImageUrls], [...oldImageUrls]);

    // upload auction sheet
    const uploadedAuctionSheet = await orderImagesUpload(fullChassis, [...auctionSheet]);
    const deletedAuctionSheet = await deleteImages([...newAuctionImage], [...oldAuctionImage]);

    if (deletedImages || deletedAuctionSheet) {
      // set error message
      onError(!!id ? "Stock not updated!" : "Stock not created!");
      // loading end
      setOnSubmitLoading(false);
      return;
    }

    // Convert object values to an array
    const objectValuesArray = map(rest, "value");

    // Convert array of object values to an array
    const arrayValuesArray = flatMap(features, "value");

    const carInfos = compact([...objectValuesArray, ...arrayValuesArray]);

    const car_info_id = chassis_code?.value;

    // create or update values
    const values = {
      chassis,
      images: uploadedImages,
      auction_sheet: uploadedAuctionSheet,
      carInfos,
      car_info_id,
      asking_price: asking_price ? Number(asking_price) : null,
      discount_price: discount_price ? Number(discount_price) : null,
      forward_price: forward_price ? Number(forward_price) : null,
      eta_date: eta_date ? dateTime(eta_date, "yyyy-MM-dd") : null,
      mileage: mileage ? Number(mileage) : null
    };

    // update
    if (!!id) {
      const afterUpdated = await updateStockList({ id, ...values });

      if (afterUpdated?.status === 200) {
        onBack();
      }
      // loading end
      setOnSubmitLoading(false);
      return;
    }

    // create
    const afterCreated = await createStockList({ ...values });
    // loading end
    setOnSubmitLoading(false);

    if (afterCreated?.status === 201) {
      onBack();
    }
  };

  const isLoading = loading || updating || onSubmitLoading;

  const title = `${!!id ? "Edit" : "Add"} Stock`;

  const breadCrumbs = [
    {
      name: "Stock List",
      link: "/stock-list"
    },
    {
      name: title
    }
  ];

  return (
    <PageLayout title={title} breadCrumbs={breadCrumbs}>
      {<Loading loading={isLoading} />}
      <div className="">
        <p>Images</p>
        <ImageUploader images={images} setImages={setImages} />
      </div>
      <div className="mt-3">
        <p>Auction Sheet</p>
        <ImageUploader singleImage={true} images={auctionSheet} setImages={setAuctionSheet} />
      </div>

      <Form
        fields={fields}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        submitbtnText={isLoading ? `${!!id ? "Editing..." : "Adding...."}` : title}
        loading={typeof isLoading === "boolean" ? isLoading : undefined}
        closebtnText={"Back"}
        onClose={onBack}
      />
    </PageLayout>
  );
}
